import React, { useEffect, useRef } from 'react';

const AnimatedBackground = ({ isLoginPage = false }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animationFrameId;

    // Set canvas dimensions with higher resolution for retina displays
    const setCanvasDimensions = () => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      ctx.scale(dpr, dpr);
    };

    setCanvasDimensions();
    window.addEventListener('resize', setCanvasDimensions);

    // Create particles
    const particlesArray = [];
    const numberOfParticles = 40; // Reduced for a cleaner, professional look

    // Color palette inspired by the image (dark purples, blues with subtle glow)
    const colors = [
      { r: 88, g: 66, b: 124, opacity: 0.2 }, // Purple-blue tone
      { r: 70, g: 80, b: 150, opacity: 0.15 }, // Darker blue
      { r: 100, g: 90, b: 180, opacity: 0.1 }, // Lighter purple-blue
    ];

    class Particle {
      constructor() {
        this.x = Math.random() * canvas.width / window.devicePixelRatio;
        this.y = Math.random() * canvas.height / window.devicePixelRatio;
        this.size = Math.random() * 1 + 0.5; // Smaller particles for subtlety
        this.speedX = Math.random() * 0.1 - 0.05; // Slower, smoother movement
        this.speedY = Math.random() * 0.1 - 0.05;

        // Random color from the palette
        const colorIndex = Math.floor(Math.random() * colors.length);
        const color = colors[colorIndex];
        this.color = `rgba(${color.r}, ${color.g}, ${color.b}, ${color.opacity})`;

        this.glow = this.size > 0.8; // Subtle glow for slightly larger particles
      }

      update() {
        this.x += this.speedX;
        this.y += this.speedY;

        // Wrap around edges for seamless movement
        if (this.x > canvas.width / window.devicePixelRatio) this.x = 0;
        else if (this.x < 0) this.x = canvas.width / window.devicePixelRatio;
        if (this.y > canvas.height / window.devicePixelRatio) this.y = 0;
        else if (this.y < 0) this.y = canvas.height / window.devicePixelRatio;
      }

      draw() {
        ctx.fillStyle = this.color;

        if (this.glow) {
          ctx.shadowBlur = this.size * 3;
          ctx.shadowColor = this.color;
        }

        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();

        if (this.glow) {
          ctx.shadowBlur = 0;
          ctx.shadowColor = 'transparent';
        }
      }
    }

    const init = () => {
      particlesArray.length = 0;
      for (let i = 0; i < numberOfParticles; i++) {
        particlesArray.push(new Particle());
      }
    };

    init();

    // Animation loop
    const animate = () => {
      // Subtle fade effect for smooth transitions
      ctx.fillStyle = 'rgba(10, 10, 20, 0.1)'; // Dark background with slight transparency
      ctx.fillRect(0, 0, canvas.width / window.devicePixelRatio, canvas.height / window.devicePixelRatio);

      for (let i = 0; i < particlesArray.length; i++) {
        particlesArray[i].update();
        particlesArray[i].draw();
      }

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    // Cleanup
    return () => {
      window.removeEventListener('resize', setCanvasDimensions);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <div className="fixed top-0 left-0 w-full h-full z-[-1] bg-gradient-to-br from-[#0A0A14] to-[#1A1A2E]">
      <canvas ref={canvasRef} className="block w-full h-full" />
    </div>
  );
};

export default AnimatedBackground;